import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { Message, SessionConfig, SessionStorageType } from '../types';
import type { StorageScope } from '../constants/storage';
import {
  loadSessionMetadata,
  saveSessionMetadata,
  loadSession,
  saveSession,
  deleteSession,
  createSessionId,
  setCurrentSessionId,
  getCurrentSessionId,
  deriveSessionTitle,
  deriveSessionPreview,
  type SessionData,
  type SessionMetadata,
} from '../utils/sessionHistory';
import { sanitizeMessagesForStorage } from '../utils/messageStorage';
import { clearThreadToken } from '../utils/threadCapability';

const AUTO_SAVE_DELAY_MS = 300;

interface UseSessionHistoryOptions {
  active: boolean;
  maxSessions: number;
  setMessages: (msgs: Message[]) => void;
  chatDispatch: (action: { type: 'SET_INPUT'; input: string }) => void;
  setThreadId: (id: string | null) => void;
  uiDispatch: (action: { type: 'CLEAR_ATTACHMENTS' } | { type: 'CLEAR_ATTACHMENT_ERRORS' }) => void;
  messages: Message[];
  threadId: string | null;
  sessionConfig?: SessionConfig;
  /** Namespaces the session list + current-session keys per org/agent (legacy global keys when absent). */
  storageScope?: StorageScope;
  /** Cancels work belonging to the outgoing conversation before a session switch. */
  onBeforeChange?: () => void;
}

export function useSessionHistory({
  active,
  maxSessions,
  setMessages,
  chatDispatch,
  setThreadId,
  uiDispatch,
  messages,
  threadId,
  sessionConfig,
  storageScope,
  onBeforeChange,
}: UseSessionHistoryOptions) {
  const beforeChangeRef = useRef(onBeforeChange);
  beforeChangeRef.current = onBeforeChange;
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessions, setSessions] = useState<SessionMetadata[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const storageKind = sessionConfig?.storage === 'session' ? 'session' : undefined;
  const scoped = !!storageScope;
  const scopeOrgId = storageScope?.orgId;
  const scopeAgentId = storageScope?.agentId;

  // The storage functions bound to this widget's storage kind and scope. Legacy
  // call shapes (no trailing args) are kept when nothing is configured. Per-session
  // data keys stay global: session ids are unique, and it lets a migrated scoped
  // list keep resolving the sessions it points at.
  const store = useMemo(() => {
    const kindArgs: [SessionStorageType?] = storageKind ? [storageKind] : [];
    const listArgs: [SessionStorageType?, StorageScope?] = scoped
      ? [storageKind ?? 'local', { orgId: scopeOrgId, agentId: scopeAgentId }]
      : kindArgs;
    return {
      loadMeta: () => loadSessionMetadata(...listArgs),
      saveMeta: (list: SessionMetadata[]) => saveSessionMetadata(list, ...listArgs),
      load: (id: string) => loadSession(id, ...kindArgs),
      save: (data: SessionData) => saveSession(data, ...kindArgs),
      remove: (id: string, retainedSessionIds: string[] = []) => {
        try {
          const removedThreadId = loadSession(id, ...kindArgs)?.threadId;
          const shared =
            removedThreadId &&
            retainedSessionIds.some(
              retainedId => loadSession(retainedId, ...kindArgs)?.threadId === removedThreadId
            );
          if (!shared)
            clearThreadToken(removedThreadId, {
              storage: storageKind ?? 'local',
            });
        } catch {
          /* Still attempt deletion if storage blocks the capability lookup. */
        }
        deleteSession(id, ...kindArgs);
      },
      getCurrentId: () => getCurrentSessionId(...listArgs),
      setCurrentId: (id: string | null) => setCurrentSessionId(id, ...listArgs),
    };
  }, [storageKind, scoped, scopeOrgId, scopeAgentId]);

  // Puts `metadata` at the head of the list, enforces maxSessions (deleting the
  // trimmed sessions), persists the list and makes that session current.
  const commitMetadata = useCallback(
    (metadata: SessionMetadata, baseList: SessionMetadata[]) => {
      const merged = [metadata, ...baseList.filter(meta => meta.id !== metadata.id)].sort((a, b) =>
        b.lastModified.localeCompare(a.lastModified)
      );
      const trim = maxSessions > 0 && merged.length > maxSessions;
      const list = trim ? merged.slice(0, maxSessions) : merged;
      store.saveMeta(list);
      if (trim) {
        const retainedIds = list.map(meta => meta.id);
        merged.slice(maxSessions).forEach(meta => store.remove(meta.id, retainedIds));
      }
      setSessions(list);
      store.setCurrentId(metadata.id);
    },
    [maxSessions, store]
  );

  // Debounced auto-save. The pending closure carries the session it belongs to so
  // a session switch flushes the old session's last state instead of dropping it.
  const pendingSaveRef = useRef<{ sessionId: string; run: () => void } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingSaveRef.current;
    if (!pending) return;
    pendingSaveRef.current = null;
    pending.run();
  }, []);

  const createNewSession = useCallback(
    (existing?: SessionMetadata[], resetThread = true) => {
      beforeChangeRef.current?.();
      flushSave();
      const now = new Date().toISOString();
      const id = createSessionId();
      const title = 'New conversation';
      const sessionData: SessionData = {
        id,
        title,
        createdAt: now,
        lastModified: now,
        messages: [] as Message[],
        threadId: null,
      };
      const metadata: SessionMetadata = {
        id,
        title,
        createdAt: now,
        lastModified: now,
        messageCount: 0,
        preview: '',
      };

      store.save(sessionData);
      commitMetadata(metadata, existing ?? store.loadMeta());
      setActiveSessionId(id);
      setMessages([]);
      chatDispatch({ type: 'SET_INPUT', input: '' });
      // Host selection/creation owns the thread transition; resetting it here can
      // cancel the new creation or briefly restore a controlled host's old id.
      if (resetThread) setThreadId(null);
      uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      setSessionsOpen(false);
      return id;
    },
    [flushSave, commitMetadata, store, setMessages, chatDispatch, setThreadId, uiDispatch]
  );

  const selectSession = useCallback(
    (id: string) => {
      // Persist the outgoing session's last state before switching.
      flushSave();
      const data = store.load(id);
      if (!data) return;
      beforeChangeRef.current?.();
      setActiveSessionId(id);
      store.setCurrentId(id);
      setMessages(data.messages || []);
      chatDispatch({ type: 'SET_INPUT', input: '' });
      setThreadId(data.threadId ?? null);
      uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      setSessionsOpen(false);
    },
    [flushSave, store, setMessages, chatDispatch, setThreadId, uiDispatch]
  );

  const removeSession = useCallback(
    (id: string) => {
      // A queued save for the removed session must not resurrect it.
      if (pendingSaveRef.current?.sessionId === id) pendingSaveRef.current = null;
      const updated = store.loadMeta().filter(meta => meta.id !== id);
      store.remove(
        id,
        updated.map(meta => meta.id)
      );
      store.saveMeta(updated);
      setSessions(updated);
      if (id === activeSessionId) {
        createNewSession(updated);
      }
    },
    [activeSessionId, createNewSession, store]
  );

  // Initialize: load session list and restore active session
  useEffect(() => {
    if (!active) return;
    const metadata = store.loadMeta();
    setSessions(metadata);
    const storedId = store.getCurrentId();
    const valid = storedId && metadata.some(meta => meta.id === storedId) ? storedId : null;
    if (!valid) {
      createNewSession(metadata);
      return;
    }
    setActiveSessionId(valid);
    const data = store.load(valid);
    if (data?.messages) setMessages(data.messages);
    if (data?.threadId !== undefined) setThreadId(data.threadId);
  }, [active, createNewSession, store, setMessages, setThreadId]);

  // Auto-save: persist the current session 300ms after the last messages/threadId
  // change (flushed on unmount, pagehide and session switch).
  useEffect(() => {
    if (!active) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      pendingSaveRef.current = null;
      return;
    }
    if (!activeSessionId) return;
    if (pendingSaveRef.current && pendingSaveRef.current.sessionId !== activeSessionId) {
      flushSave();
    }
    pendingSaveRef.current = {
      sessionId: activeSessionId,
      run: () => {
        const now = new Date().toISOString();
        const existing = store.load(activeSessionId);
        const createdAt = existing?.createdAt || now;
        const title = deriveSessionTitle(messages);
        const preview = deriveSessionPreview(messages);
        const storedMessages = sanitizeMessagesForStorage(messages);
        const sessionData: SessionData = {
          id: activeSessionId,
          title,
          createdAt,
          lastModified: now,
          messages: storedMessages,
          threadId: threadId ?? null,
        };
        store.save(sessionData);

        commitMetadata(
          {
            id: activeSessionId,
            title,
            createdAt,
            lastModified: now,
            messageCount: messages.length,
            preview,
          },
          store.loadMeta()
        );
      },
    };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushSave, AUTO_SAVE_DELAY_MS);
  }, [active, activeSessionId, commitMetadata, flushSave, messages, store, threadId]);

  // The debounce window must not swallow the final write.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('pagehide', flushSave);
    return () => {
      window.removeEventListener('pagehide', flushSave);
      flushSave();
    };
  }, [flushSave]);

  return {
    sessionsOpen,
    setSessionsOpen,
    sessions,
    activeSessionId,
    createNewSession,
    selectSession,
    removeSession,
  };
}
